import { Link, useLocation } from "react-router-dom";
import { InputField } from "@components/Input/InputField";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, Form, Modal, Stack } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { loginAction, resetPasswordResetAction } from "@redux/actions";
import { Fragment, useEffect, useLayoutEffect } from "react";
import { APP_TITLE_SUFFIX, EMAIL_VALIDATE, REMEMBER_ME } from "@constants";
import styles from "./styles/SignIn.module.css";
import { /* LoginWithFacebook */ LoginWithGoogle } from "./components";
import Cookies from "js-cookie";
import { useState } from "react";

const validateSchema = Yup.object().shape({
  email: Yup.string()
    .required("Email Address is required")
    .matches(EMAIL_VALIDATE, "Invalid email format"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password require minimum of 8 characters")
    .max(255, "Password require maximum of 255 characters"),
});

const initialValues = { email: "", password: "", isRemember: false };

const SignIn = () => {
  // state
  const [blockedModalVSB, setBlockedModalVSB] = useState(false);

  // variables
  let remembered = false;
  let rememberedLoginValues = null;
  let targetUserName = null;
  const inputFields = [
    {
      name: "email",
      type: "email",
      placeholder: "Email Address",
      prefix: <i className="bi bi-envelope" />,
    },
    {
      name: "password",
      type: "password",
      placeholder: "Password",
      prefix: <i className="bi bi-key" />,
    },
  ];
  if (Cookies.get(REMEMBER_ME)) {
    rememberedLoginValues = JSON.parse(Cookies.get(REMEMBER_ME));
    remembered = rememberedLoginValues.isRemember;
  }

  // hooks
  const { state } = useLocation();
  const { login } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const {
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldError,
    setFieldValue,
    values,
    errors,
    touched,
  } = useFormik({
    initialValues: state || rememberedLoginValues || initialValues,
    validationSchema: validateSchema,
    onSubmit: (values) => {
      dispatch(loginAction(values));
    },
  });

  // logics
  if (state && state.fullname) targetUserName = state.fullname;

  if (remembered) targetUserName = rememberedLoginValues.email.split("@")[0];

  const handleHideBlocked = () => setBlockedModalVSB(false);
  const handleShowBlocked = () => setBlockedModalVSB(true);

  // UI partials
  const loginForm = (
    <Form noValidate onSubmit={handleSubmit}>
      <Stack gap={4}>
        {/* <LoginWithFacebook /> */}
        <LoginWithGoogle />
        <span className="text-muted">Or use your Cursus account!</span>
        {inputFields.map((field, index) => (
          <Form.Group key={index} controlId={field.name}>
            <InputField
              name={field.name}
              type={field.type}
              placeholder={field.placeholder}
              value={values[field.name]}
              onChange={handleChange}
              onBlur={handleBlur}
              touchedValue={touched[field.name]}
              errorsValue={errors[field.name]}
              prefix={field.prefix || ""}
            />
          </Form.Group>
        ))}
        <Form.Group>
          <Stack direction="horizontal" className="justify-content-between">
            <Form.Check type="checkbox" id="isRemember">
              <Form.Check.Input
                type="checkbox"
                checked={values.isRemember}
                onChange={() => setFieldValue("isRemember", !values.isRemember)}
              />
              <Form.Check.Label>Remember me</Form.Check.Label>
            </Form.Check>
            <Link to={"/forgot-password"} state={{ email: values.email }}>
              Forgot Password
            </Link>
          </Stack>
        </Form.Group>
        <Form.Group controlId="submit">
          <Button type="submit" style={{ width: "100%" }}>
            Sign In
          </Button>
        </Form.Group>
      </Stack>
    </Form>
  );

  // effects
  useEffect(() => {
    if (!login.processing && login.isFailed) {
      if (!login.isBlocked) {
        setFieldError("email", "Incorrect email or password");
        setFieldError("password", "Incorrect email or password");
      } else {
        handleShowBlocked();
      }
    }
  }, [login.processing]);

  useLayoutEffect(() => {
    dispatch(resetPasswordResetAction());
    document.title = `Sign In | ${APP_TITLE_SUFFIX}`;
    return () => {
      document.title = APP_TITLE_SUFFIX;
    };
  }, []);

  // render
  return (
    <Fragment>
      <Modal show={blockedModalVSB} onHide={handleHideBlocked}>
        <Modal.Header closeButton>
          <Modal.Title>Account Blocked</Modal.Title>
        </Modal.Header>
        <Modal.Body>Your account has been blocked by Admin.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleHideBlocked}>
            Close
          </Button>
          <Button variant="primary" onClick={handleHideBlocked}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>
      <Stack gap={4} className={styles.container}>
        <Stack>
          <h2>
            <b>
              Welcome Back,{" "}
              {(targetUserName && [...targetUserName.split(" ")].pop()) ||
                "my friend"}
            </b>
          </h2>
          <span>Let's log In to Your Account!</span>
        </Stack>
        {loginForm}
        <div>
          Don't have an account? <Link to={"/sign-up"}>Sign Up</Link>
        </div>
      </Stack>
    </Fragment>
  );
};

/* required: display name */
SignIn.displayName = "SignIn";

/* required: props types */
SignIn.propTypes = {};

/* required: default props */
SignIn.defaultProps = {};

export { SignIn };
